import { Collapsible } from "@knowtex-ai/components";
import "./tokens.css";

/**
 * FoiaTimeline — a presentational timeline built on @knowtex-ai/components.
 *
 * Inputs are data only:
 *  - events: ordered [{ id, stepKey, message, timestamp, actor: { type, label },
 *            isExternallyVisible, badge?: { label, tone } }]
 *  - steps: ordered step definitions [{ key, title, waitingOnLabel, actionUiType }]
 *  - currentStepKey: the key of the step currently in progress
 *  - mode: "internal" (show everything) | "external" (hide events that are
 *          not externally visible; a step with none left is hidden entirely)
 *  - renderAction(stepKey): renders the current step's action UI
 *
 * It has no knowledge of routing, auth, or app state — everything it needs
 * arrives as props.
 */
export default function FoiaTimeline({ events, steps, currentStepKey, mode = "internal", renderAction }) {
  const currentIndex = steps.findIndex((step) => step.key === currentStepKey);

  return (
    <ol className="foia-timeline" aria-label="FOIA request timeline">
      {steps.map((step, index) => {
        const allStepEvents = events.filter((event) => event.stepKey === step.key);
        const visibleEvents =
          mode === "external" ? allStepEvents.filter((event) => event.isExternallyVisible) : allStepEvents;

        const isDone = allStepEvents.length > 0 && index !== currentIndex;
        const isCurrent = index === currentIndex;

        if (isDone && mode === "external" && visibleEvents.length === 0) {
          return null;
        }

        return (
          <TimelineStep
            key={step.key}
            step={step}
            events={visibleEvents}
            status={isDone ? "done" : isCurrent ? "pending" : "upcoming"}
            isLast={index === steps.length - 1}
            renderAction={isCurrent && renderAction ? () => renderAction(step.key) : null}
          />
        );
      })}
    </ol>
  );
}

function TimelineStep({ step, events, status, isLast, renderAction }) {
  const isExpandable = status === "done" && events.length > 0;

  return (
    <li className={`foia-timeline__item foia-timeline__item--${status}`}>
      {!isLast && <span className="foia-timeline__connector" aria-hidden="true" />}
      <span className={`foia-timeline__marker foia-timeline__marker--${status}`} aria-hidden="true">
        {status === "done" ? <CheckGlyph /> : status === "pending" ? <DotGlyph pulse /> : <DotGlyph />}
      </span>

      <div className="foia-timeline__card">
        {isExpandable ? (
          <Collapsible
            className="foia-timeline__collapsible"
            defaultOpen={false}
            trigger={<span className="foia-timeline__title">{step.title}</span>}
          >
            <EventList events={events} />
          </Collapsible>
        ) : (
          <>
            <div className="foia-timeline__header">
              <span className="foia-timeline__title">{step.title}</span>
              {status === "pending" && step.waitingOnLabel && (
                <span className="foia-timeline__waiting-on">Waiting on {step.waitingOnLabel}</span>
              )}
            </div>

            <div className="foia-timeline__body">
              {status === "upcoming" && (
                <p className="foia-timeline__muted">
                  {step.waitingOnLabel ? `Will wait on ${step.waitingOnLabel}.` : "Not started yet."}
                </p>
              )}

              <EventList events={events} />

              {status === "pending" && renderAction && <div className="foia-timeline__action">{renderAction()}</div>}
            </div>
          </>
        )}
      </div>
    </li>
  );
}

function EventList({ events }) {
  if (events.length === 0) return null;
  return (
    <ul className="foia-timeline__events">
      {events.map((event) => (
        <li key={event.id} className="foia-timeline__event">
          <p className="foia-timeline__event-message">{event.message}</p>
          <div className="foia-timeline__event-meta">
            <span className="foia-timeline__event-actor">{event.actor?.label}</span>
            <span className="foia-timeline__event-dot" aria-hidden="true">
              •
            </span>
            <time className="foia-timeline__event-time" dateTime={event.timestamp}>
              {formatTimestamp(event.timestamp)}
            </time>
            {event.badge && (
              <span className={`foia-timeline__badge foia-timeline__badge--${event.badge.tone || "neutral"}`}>
                {event.badge.label}
              </span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatTimestamp(isoString) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function CheckGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DotGlyph({ pulse }) {
  return <span className={`foia-timeline__dot${pulse ? " foia-timeline__dot--pulse" : ""}`} />;
}
